import React, { useState, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import Popover, { PopoverChildrenProps } from '../Popover'
import { noop } from 'utils'
import { Placement } from '@popperjs/core'
import { InView } from 'react-intersection-observer'
import { Modifier } from 'react-popper'

interface DropdownStateProps {
  contentRef?: any
  onToggle: (visible: boolean) => void
}

interface DropdownStateReturn {
  visible: boolean
  show: () => void
  hide: () => void
  toggleDropdown: () => void
}

interface DropdownState {
  (props: DropdownStateProps): DropdownStateReturn
}

export const useDropdownState: DropdownState = ({ onToggle = noop }) => {
  const [visible, setVisible] = useState(false)

  const show = useCallback(() => {
    if (!visible) {
      onToggle(true)
      setVisible(true)
    }
  }, [onToggle, visible])

  const hide = useCallback(() => {
    if (visible) {
      onToggle(false)
      setVisible(false)
    }
  }, [onToggle, visible])

  const toggleDropdown = useCallback(() => {
    visible ? hide() : show()
  }, [hide, show, visible])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide()
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  })

  return { visible, show, hide, toggleDropdown }
}

interface StatelessDropdownProps {
  children: React.ReactNode
  toggleDropdown: () => void
  className?: string
  visible?: boolean
  minWidth?: string | number
  maxWidth?: string | number
  toggle: PopoverChildrenProps
  style?: React.CSSProperties
  contentPadding?: string
  placement?: Placement
  modifiers?: Modifier<any>[]
  contentStyle?: React.CSSProperties
  spacing?: string // space between the trigger and the dropdown content
  onClickOutside: (e: MouseEvent) => void
  usePortal?: boolean
}

export const StatelessDropdown = ({
  style = {},
  minWidth = '160px',
  maxWidth = '352px',
  contentPadding = '0.5rem 0',
  children,
  toggleDropdown,
  toggle,
  visible,
  className,
  onClickOutside,
  usePortal = false,
  ...props
}: StatelessDropdownProps) => {
  // The negative margin on the root element will trigger the observable sooner.
  // Since, in this case, the root element is the "window", this margin is
  // the size of the Header height (64px) plus some adjustments needed
  // to trigger the observable on the desired position.
  const ROOT_MARGIN = '-94px 0px 0px 0px'

  const handleObservableChange = (inView: boolean) => {
    if (visible && !inView) {
      toggleDropdown()
    }
  }

  return (
    <InView
      rootMargin={ROOT_MARGIN}
      onChange={handleObservableChange}
      className={className}>
      {({ ref }) => (
        <Popover
          popup={
            <div ref={ref} style={{ minWidth, maxWidth }}>
              {children}
            </div>
          }
          spacing="18px"
          style={style}
          contentPadding={contentPadding}
          onClickOutside={onClickOutside}
          usePortal={usePortal}
          onClick={(e: Event) => {
            e.stopPropagation()
            e.preventDefault()
            toggleDropdown()
          }}
          className={className}
          visible={visible}
          {...props}>
          {toggle}
        </Popover>
      )}
    </InView>
  )
}

export interface DropdownProps {
  children: React.ReactNode
  onToggle?: (visible: boolean) => void
  minWidth?: string | number
  maxWidth?: string | number
  toggle: React.ReactNode | ((visible: boolean) => React.ReactNode)
  style?: React.CSSProperties
  contentPadding?: string
  contentStyle?: React.CSSProperties
  disabled?: boolean
  placement?: Placement
  modifiers?: Modifier<any>[]
  spacing?: string
  className?: string
  usePortal?: boolean
  hideOnClick?: boolean
  visible?: boolean
}

/**
 * The Dropdown is used in place of a Select when we want to have more customizeable options.
 *
 * It can have different placements, you can check all of them via the Properties table controls.
 */
const Dropdown = ({
  children,
  toggle,
  style = {},
  minWidth = '160px',
  maxWidth = '352px',
  contentPadding = '0.5rem 0',
  onToggle = noop,
  disabled = false,
  usePortal = false,
  hideOnClick = true,
  ...props
}: DropdownProps) => {
  const { visible, hide, toggleDropdown } = useDropdownState({
    onToggle,
  })

  const optionsRef = useRef<HTMLUListElement>(null)
  const tabPressCounter = useRef(0)
  const previouslyFocused = useRef() as React.MutableRefObject<HTMLElement>

  const findFirstElement = useCallback(
    (element: Element): HTMLElement | null => {
      return element?.querySelector('a:not(:disabled), button:not(:disabled)')
    },
    []
  )

  useEffect(() => {
    const refInstance = optionsRef?.current

    if (!refInstance) {
      return
    }

    // after opening a dropdown and pressing tab, force focus
    // on first focusable element of the dropdown options
    const handlePressTab = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && visible) {
        if (!previouslyFocused?.current && document.activeElement) {
          previouslyFocused.current = document.activeElement as HTMLElement
        }

        event.preventDefault()

        const optionsLength = refInstance ? refInstance?.childElementCount : 0

        // skip the separator
        if (
          refInstance?.children[tabPressCounter.current % optionsLength]
            .localName === 'div'
        ) {
          tabPressCounter.current = tabPressCounter.current + 1
        }

        // loop through the dropdown options
        findFirstElement(
          refInstance?.children[tabPressCounter.current % optionsLength]
        )?.focus()

        tabPressCounter.current = tabPressCounter.current + 1
      }
    }

    // only use the event listener if the dropdown is globally positioned
    if (usePortal) {
      document.addEventListener('keydown', handlePressTab)
    }

    return () => {
      previouslyFocused?.current?.focus()

      return document.removeEventListener('keydown', handlePressTab)
    }
  }, [optionsRef, visible, usePortal, previouslyFocused, findFirstElement])

  const isVisible = !disabled && visible

  return (
    <StatelessDropdown
      visible={isVisible}
      toggleDropdown={toggleDropdown}
      toggle={typeof toggle === 'function' ? toggle(isVisible) : toggle}
      style={style}
      minWidth={minWidth}
      maxWidth={maxWidth}
      contentPadding={contentPadding}
      onClickOutside={hide}
      usePortal={usePortal}
      {...props}>
      <DropdownOptionsList
        ref={optionsRef}
        onClick={hideOnClick ? hide : e => e.preventDefault()}
        data-testid="dropdown-options">
        {children}
      </DropdownOptionsList>
    </StatelessDropdown>
  )
}

export default Dropdown

const DropdownOptionsList = styled.ul`
  padding: 0;
  margin: 0;
`
